import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { haptics } from '@/lib/haptics';
import { Camera, X, Loader2, ChevronRight, ChevronLeft, ChevronDown, MessageCircle, ArrowLeftRight, Truck, Phone, TrendingUp, Trash2, ShieldCheck, Shield, Check, Images, MapPin, AlertTriangle, Upload, CalendarIcon, Sparkles, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Link } from 'react-router-dom';
import { ItemImageEditorDialog } from '@/components/item/ItemImageEditorDialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ProofPackUpload } from '@/components/profile/ProofPackUpload';
import { ProofPackGallery } from '@/components/profile/ProofPackGallery';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PRICE_INPUT_PROPS } from '@/lib/priceInputProps';
import { Textarea } from '@/components/ui/textarea';
import { FloatingInput, FloatingTextarea, FloatingSelectField } from '@/components/ui/floating-input';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { LocationValue } from '@/components/ui/location-autocomplete';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MobileFullscreenPage } from '@/components/layout/MobileFullscreenPage';
import { useIsMobile } from '@/hooks/use-mobile';
import { useVisualViewportInset } from '@/hooks/useVisualViewportInset';
import { GradingCompanyCombobox } from '@/components/shared/GradingCompanyCombobox';
import { SlabGradeInput } from '@/components/item/SlabGradeInput';
import { LanguageMultiSelectField } from '@/components/shared/LanguageMultiSelectField';
import { TermsAcceptanceModal } from '@/components/auth/TermsAcceptanceModal';

import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/lib/toast';
import { useCreateUserItem, useUpdateUserItem, useUploadItemImages, useDeleteUserItemImage, useUploadProofPackImages } from '@/hooks/useUserItems';
import { supabase } from '@/integrations/supabase/client';
import { useTermsAcceptance } from '@/hooks/useTermsAcceptance';
import { HIGH_VALUE_LISTING_CENTS } from '@/lib/legalConstants';

import { CATEGORY_LABELS, SINGLES_CONDITION_LABELS, SEALED_CONDITION_LABELS, ACCESSORY_CONDITION_LABELS, ACCESSORY_TYPE_LABELS, LOT_TYPE_LABELS, ListingCategory, SinglesCondition, SealedCondition, AccessoryCondition, AccessoryType, LotType } from '@/types/listing';
import { PRODUCT_TYPE_OPTIONS, ScProductType, UserItem, UserItemImage } from '@/types/userItem';
import { getSinglesCondition, getSealedCondition, normalizeLanguage } from '@/components/itemForm/itemFormHelpers';
import { FastAddRow } from '@/components/itemForm/FastAddRow';
import { TRADING_CARD_GAMES } from '@/constants/tradingCardGames';
import { LANGUAGE_OPTIONS, CardLanguage } from '@/constants/cardLanguages';
import { ERROR_TYPE_OPTIONS } from '@/constants/cardErrorTypes';
import { RARITY_OPTIONS } from '@/constants/cardRarities';
import { FINISH_OPTIONS } from '@/constants/cardFinishes';
import { FinishCombobox } from '@/components/ui/finish-combobox';
import { GRADING_COMPANY_MAP } from '@/constants/gradingCompanies';
import { goldGradientActive } from '@/lib/gradientStyles';
import { resolveCollectrPriceCents, formatCents } from '@/lib/collectrPricing';
import { normalizeCardNumber } from '@/lib/cardNumberUtils';
import { cn } from '@/lib/utils';
import { ImageSelectionDialog } from './ImageSelectionDialog';
import { useDuplicateDetection } from '@/hooks/useDuplicateDetection';
import { itemFormSchema, STEP_LABELS, type ItemFormData } from '@/components/itemForm/itemFormSchema';
import { InvestmentTrackingSection } from '@/components/itemForm/InvestmentTrackingSection';
import { ConfirmDeleteImageDialog } from '@/components/itemForm/ConfirmDeleteImageDialog';
import { DeleteItemsConfirmDialog } from '@/components/inventory/DeleteItemsConfirmDialog';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';

import { useItemFormImages } from '@/components/itemForm/useItemFormImages';

interface ItemFormModalProps {
  item?: UserItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultInvestmentOpen?: boolean;
  /** 'item' (single, default) or 'lot' (bulk lot entry). Only honored in Add mode. */
  initialMode?: 'item' | 'lot';
}

export function ItemFormModal({ item, open, onOpenChange, defaultInvestmentOpen, initialMode = 'item' }: ItemFormModalProps) {
  const isEditMode = !!item;
  const { user } = useAuth();
  
  // Hooks for mutations
  const createItem = useCreateUserItem();
  const updateItem = useUpdateUserItem();
  const uploadImages = useUploadItemImages();
  const deleteImage = useDeleteUserItemImage();
  const uploadProofImages = useUploadProofPackImages();
  const { hasAcceptedTerms, refreshTermsStatus } = useTermsAcceptance();
  
  // Single-item Add mode collapses Quick Add at top + expandable rest below.
  // `expanded` flips when the user taps "Continue editing". In edit mode and
  // lot mode the full form is always rendered, so expanded is forced true.
  const [mode, setMode] = useState<'item' | 'lot'>(initialMode);
  const [expanded, setExpanded] = useState<boolean>(isEditMode || initialMode === 'lot');
  
  // Profile location (fetched from user profile)
  const [profileLocation, setProfileLocation] = useState<{ city: string; stateCode: string; lat?: number; lng?: number } | null>(null);
  const [profileWillShip, setProfileWillShip] = useState(false);
  const [profileUsername, setProfileUsername] = useState<string | null>(null);
  
  // ---- Image state + handlers (add/edit uploads, stock, drag/drop, editor) ----
  const img = useItemFormImages();
  const {
    newImages, newImagePreviews, existingImages, imagesToDelete,
    stockImageUrl, stockPosition, photoOrderDirty,
    editingImageIndex, editingImageSrc, confirmDeleteOpen,
    dragIndex, touchDragIndex, isDraggingPhotos, cameraInputRef,
    setNewImages, setNewImagePreviews, setStockImageUrl, setConfirmDeleteOpen,
    visibleExistingImages, uploadedImageCount, hasStockImage, totalImageCount, orderableImages,
    addRawFiles, handleImageChange,
    handlePhotosDragEnter, handlePhotosDragOver, handlePhotosDragLeave, handlePhotosDrop,
    removeNewImage, handleRemoveExistingImage, confirmRemoveImage, moveExistingImage,
    handleDragStart, handleDragOver, handleDragEnd,
    handleTouchStart, handleTouchMove, handleTouchEnd,
    handleRemoveUploadedImage, handleRemoveStockImage,
    handleImageClick, closeEditor, handleImageEdited,
    initForEdit, resetForAdd,
  } = img;

  const [deleteItemConfirmOpen, setDeleteItemConfirmOpen] = useState(false);
  const [isDeletingItem, setIsDeletingItem] = useState(false);
  const queryClient = useQueryClient();

  const handleDeleteThisItem = async () => {
    if (!item) return;
    setIsDeletingItem(true);
    try {
      const { endEbayListingsForItems } = await import('@/lib/endEbayListingsForItems');
      await endEbayListingsForItems([item.id]);
      const { error } = await supabase.from('user_items').delete().eq('id', item.id);
      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: ['my-items', item.user_id] });
      await queryClient.invalidateQueries({ queryKey: ['user-item', item.id] });
      await queryClient.invalidateQueries({ queryKey: ['marketplace-items'] });
      toast.success('Item deleted');
      setDeleteItemConfirmOpen(false);
      onOpenChange(false);
    } catch (err: any) {
      console.error('Failed to delete item:', err);
      toast.error(err?.message || 'Failed to delete item');
    } finally {
      setIsDeletingItem(false);
    }
  };
  
  
  
  // Proof Pack state
  const [proofImages, setProofImages] = useState<Record<string, File>>({});
  const [existingProofImages, setExistingProofImages] = useState<Record<string, string>>({});
  
  
  const [proofPackOpen, setProofPackOpen] = useState(false);
  
  // Investment tracking toggle
  const [investmentOpen, setInvestmentOpen] = useState(false);
  const [photoOrderOpen, setPhotoOrderOpen] = useState(false);
  
  const [costBasisMode, setCostBasisMode] = useState<'per_item' | 'total'>('per_item');
  const [costTrackingMode, setCostTrackingMode] = useState<'same' | 'individual'>('same');
  const [individualCosts, setIndividualCosts] = useState<string[]>([]);
  const [individualDates, setIndividualDates] = useState<string[]>([]);
  
  // Phone verification check
  const [isPhoneVerified, setIsPhoneVerified] = useState<boolean | null>(null);
  
  // Terms modal
  const [showTermsModal, setShowTermsModal] = useState(false);
  
  // Submitting state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // (Legacy fastMode/fastAutoTitle/fastContinueRef removed — Quick Add is
  // now the default, expandable inline via the `expanded` state above.)

  // Mobile sheet vs desktop dialog
  const isMobile = useIsMobile();
  const { keyboardHeight } = useVisualViewportInset();
  // (Legacy TitleComp removed; headerContent below uses TitleEl locally.)
  
  // Stock image dialog state
  const [stockImageDialogOpen, setStockImageDialogOpen] = useState(false);
  
  // Camera input ref for Take Photo
  // Lot-mode hero file input ref (parallels FastAddRow's empty-state hero)
  const lotHeroFileRef = useRef<HTMLInputElement>(null);

  // Fetch profile data (location, selling preferences)
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!user) return;
      const { data } = await supabase
        .from('profiles')
        .select('city, state_code, latitude, longitude, will_ship_items, accepts_trades, specialized_games, username')
        .eq('user_id', user.id)
        .maybeSingle();
      // Phone verification was removed platform-wide; the reveal card still
      // takes the prop but nothing surfaces a verified phone anymore.
      setIsPhoneVerified(false);
      setProfileUsername(data?.username ?? null);
      if (data?.city && data?.state_code) {
        setProfileLocation({
          city: data.city,
          stateCode: data.state_code,
          lat: data.latitude ? Number(data.latitude) : undefined,
          lng: data.longitude ? Number(data.longitude) : undefined,
        });
      }
      setProfileWillShip(data?.will_ship_items ?? false);

      // Only pre-fill defaults for new items (not edit mode)
      if (!item) {
        // Pre-fill ships_anywhere from profile setting
        form.setValue('ships_anywhere', data?.will_ship_items ?? false);
        // Pre-fill accepts_trades from profile setting
        form.setValue('accepts_trades', data?.accepts_trades ?? false);
        // Pre-fill language from local device preference (profile column dropped)
        try {
          const savedLang = window.localStorage.getItem('cs.lastListingLanguage');
          if (savedLang) form.setValue('language', savedLang);
        } catch { /* ignore */ }
        // Pre-fill game from first specialized game (if any)
        if (data?.specialized_games?.length > 0) {
          form.setValue('game', data.specialized_games[0]);
        }
      }
    };
    fetchProfileData();
  }, [user, item]);

  // Condition helpers extracted to ./itemForm/itemFormHelpers


  const form = useForm<ItemFormData>({
    resolver: zodResolver(itemFormSchema),
    defaultValues: {
      game: 'pokemon',
      language: 'english',
      title: '',
      category: 'sealed',
      sealed_condition: 'factory_sealed',
      quantity: 1,
      is_listed: false,
      // QuickList pattern — Convergence Plan Phase 2.1. Open-to-offers defaults ON
      // so a new item can be listed without picking a price (buyers message with offers).
      is_negotiable: true,
      accepts_trades: false,
      ships_anywhere: false,
      is_error_card: false,
      is_premium_grade: false,
      is_first_edition: false,
      lot_type: 'singles_lot',
      includes_holos: false,
      includes_ultras: false,
      includes_slabs: false,
      includes_promos: false,
      includes_promo_boxes: false,
      includes_etbs: false,
      includes_booster_boxes: false,
      includes_booster_packs: false,
      includes_tins: false,
      includes_premium_collections: false,
      includes_bundles: false,
      includes_booster_cases: false,
      includes_build_battle: false,
      includes_starter_decks: false,
      includes_blisters: false,
      lot_eras: [],
      lot_languages: [],
    },
  });

  // Reset form when modal opens/item changes
  // normalizeLanguage extracted to ./itemForm/itemFormHelpers


  useEffect(() => {
    if (open) {
      if (isEditMode && item) {
        // Edit mode - populate with existing item data
        const isLotCategory = item.category === 'lots';
        setMode(isLotCategory ? 'lot' : 'item');
        setExpanded(true);
        
        form.reset({
          game: item.game || 'pokemon',
          custom_game_name: (item as any).custom_game_name || '',
          language: normalizeLanguage(item.language),
          title: item.title,
          category: item.category as any,
          singles_condition: getSinglesCondition(item) as 'mint' | 'near_mint' | 'lightly_played' | 'moderately_played' | 'heavily_played' | 'damaged' | undefined,
          sealed_condition: getSealedCondition(item),
          slab_grade: item.slab_grade || undefined,
          grading_company: item.grading_company || '',
          certification_number: item.certification_number || '',
          card_number: item.card_number || '',
          rarity: item.rarity || '',
          finish: item.finish || '',
          finish_label: (item as any).finish_label || null,
          set_name: item.set_code || '',
          is_first_edition: item.is_first_edition || false,
          is_error_card: item.is_error_card || false,
          error_type: item.error_type || '',
          product_type: item.product_type || '',
          quantity: item.quantity || 1,
          purchase_date: item.purchase_date || '',
          cost_basis: item.cost_basis_cents ? (item.cost_basis_cents / 100).toString() : '',
          description: item.description || item.notes || '',
          is_listed: item.is_listed || item.visibility === 'listed',
          asking_price: item.asking_price_cents ? (item.asking_price_cents / 100).toString() : '',
          is_negotiable: item.is_negotiable || false,
          accepts_trades: item.accepts_trades || false,
          ships_anywhere: item.ships_anywhere || false,
          target_price: item.target_price_cents ? (item.target_price_cents / 100).toString() : '',
          lot_type: (item.lot_type as LotType) || 'singles_lot',
          approx_card_count: item.approx_card_count || undefined,
          box_count: item.box_count || undefined,
          lot_highlights: item.lot_highlights || '',
          lot_eras: item.lot_eras || [],
          lot_languages: item.lot_languages || [],
        });
        
        // Location is now from profile, no need to set from item
        
        // Set existing images + stock via image manager
        initForEdit({ images: item.images || [], stockImageUrl: item.stock_image_url || null });
        
        // Set proof pack state
        setProofImages({});
        setExistingProofImages((item.proof_images as Record<string, string>) || {});
        
        
        // Auto-expand investment if data exists
        const hasInvestmentData = Boolean(item.target_price_cents || item.purchase_date || item.cost_basis_cents || item.individual_costs_cents?.length);
        setInvestmentOpen(hasInvestmentData);
        setProofPackOpen(false);
        
        // Set individual costs state
        if (item.individual_costs_cents && item.individual_costs_cents.length > 0) {
          setCostTrackingMode('individual');
          setIndividualCosts(item.individual_costs_cents.map(c => (c / 100).toString()));
          // Individual dates from DB (if stored)
          const itemDates = (item as any).individual_dates as string[] | null;
          setIndividualDates(itemDates?.length ? itemDates : Array(item.individual_costs_cents.length).fill(''));
        } else {
          setCostTrackingMode('same');
          setIndividualCosts([]);
          setIndividualDates([]);
        }
      } else {
        // Add mode - reset to defaults
        setMode(initialMode);
        setExpanded(initialMode === 'lot');
        if (initialMode === 'lot') {
          form.reset({
            category: 'lots' as any,
            lot_type: 'singles_lot',
            lot_condition: 'lightly_played',
          } as any);
        } else {
          form.reset();
        }
        resetForAdd();
        setProofImages({});
        setExistingProofImages({});

        setInvestmentOpen(defaultInvestmentOpen ?? true);
        setProofPackOpen(false);
        setCostTrackingMode('same');
        setIndividualCosts([]);
        setIndividualDates([]);
      }
    }
  }, [open, item, isEditMode, form, defaultInvestmentOpen, initialMode]);

  const watchCategory = form.watch('category');
  const watchLanguage = form.watch('language') as CardLanguage;
  const watchGame = form.watch('game');
  const watchIsListed = form.watch('is_listed');
  const watchIsErrorCard = form.watch('is_error_card');
  const watchGradingCompany = form.watch('grading_company');
  const watchIsPremiumGrade = form.watch('is_premium_grade');
  const watchTargetPrice = form.watch('target_price');
  const watchAskingPrice = form.watch('asking_price');
  const watchCostBasis = form.watch('cost_basis');
  const watchQuantity = form.watch('quantity');
  const watchLotType = form.watch('lot_type');
  const watchLotLanguages = form.watch('lot_languages') || [];
  const watchIsNegotiable = form.watch('is_negotiable');

  // Auto-fill asking price from price goal when listing is toggled ON
  const prevIsListedRef = useRef(watchIsListed);
  if (watchIsListed && !prevIsListedRef.current) {
    const currentAskingPrice = form.getValues('asking_price');
    if (!currentAskingPrice && watchTargetPrice) {
      form.setValue('asking_price', watchTargetPrice);
    }
  }
  prevIsListedRef.current = watchIsListed;

  // Sync individual costs array when quantity changes
  useEffect(() => {
    if (costTrackingMode === 'individual') {
      setIndividualCosts(prev => {
        if (watchQuantity > prev.length) {
          // Add empty slots for new units
          return [...prev, ...Array(watchQuantity - prev.length).fill('')];
        } else if (watchQuantity < prev.length) {
          // Trim to match new quantity
          return prev.slice(0, watchQuantity);
        }
        return prev;
      });
    }
  }, [watchQuantity, costTrackingMode]);
  const gradingCompanyInfo = useMemo(() => {
    if (!watchGradingCompany) return null;
    return GRADING_COMPANY_MAP.get(watchGradingCompany);
  }, [watchGradingCompany]);

  const supportsHalfGrades = gradingCompanyInfo?.supportsHalfGrades ?? true;
  const premiumGradeLabel = gradingCompanyInfo?.premiumGradeLabel;


  // Proof pack calculations
  const newProofPhotoCount = Object.keys(proofImages).length;
  const existingProofPhotoCount = Object.keys(existingProofImages).length;
  const totalProofPhotos = newProofPhotoCount + existingProofPhotoCount;
  const hasProofPack = totalProofPhotos >= 3 || (isEditMode && item?.has_proof_pack);

  // Step validation
  const validateStep1 = async (): Promise<boolean> => {
    const fieldsToValidate: (keyof ItemFormData)[] = ['title', 'category'];
    
    if (mode === 'item') {
      fieldsToValidate.push('game', 'language', 'quantity');
      if (watchGame === 'other') {
        fieldsToValidate.push('custom_game_name');
      }
      // Add condition validation based on category
      if (watchCategory === 'singles') fieldsToValidate.push('singles_condition');
      if (watchCategory === 'sealed') fieldsToValidate.push('sealed_condition');
      if (watchCategory === 'accessories') fieldsToValidate.push('accessory_condition');
    }
    
    return form.trigger(fieldsToValidate);
  };

  const validateStep2 = async (): Promise<boolean> => {
    if (mode === 'lot') {
      const lotType = form.getValues('lot_type');
      if (lotType === 'singles_lot') {
        const cardCount = form.getValues('approx_card_count');
        if (!cardCount || cardCount < 1) {
          toast.error('Please enter approximate card count');
          return false;
        }
      } else if (lotType === 'sealed_lot') {
        const boxCnt = form.getValues('box_count');
        if (!boxCnt || boxCnt.trim() === '') {
          toast.error('Please list the sealed products included');
          return false;
        }
      } else if (lotType === 'mixed_lot') {
        const cardCount = form.getValues('approx_card_count');
        const boxCnt = form.getValues('box_count');
        if ((!cardCount || cardCount < 1) && (!boxCnt || boxCnt.trim() === '')) {
          toast.error('Please enter card count or sealed products list');
          return false;
        }
      }
    } else {
      // Validate category-specific fields for Step 2 (product type, slab details, accessory type)
      const fieldsToValidate: (keyof ItemFormData)[] = [];
      if (watchCategory === 'sealed') fieldsToValidate.push('product_type');
      if (watchCategory === 'slabs') fieldsToValidate.push('slab_grade', 'grading_company');
      if (watchCategory === 'accessories') fieldsToValidate.push('accessory_type');
      
      if (fieldsToValidate.length > 0) {
        const valid = await form.trigger(fieldsToValidate);
        if (!valid) return false;
      }
    }

    // Photos are optional now - no minimum requirement
    return true;
  };

  // Step machinery removed — Add Item is a single continuous form. The
  // legacy "Continue editing" disclosure inside FastAddRow flips `expanded`
  // and the rest of the fields unfold inline below it.

  const handleModeSwitch = (newMode: 'item' | 'lot') => {
    setMode(newMode);
    if (newMode === 'lot') {
      form.setValue('category', 'lots');
      form.setValue('lot_type', 'singles_lot');
      form.setValue('lot_condition', 'lightly_played');
    } else {
      form.setValue('category', 'sealed');
    }
  };

  const resetAndClose = () => {
    form.reset();
    setMode(initialMode);
    setExpanded(initialMode === 'lot');
    resetForAdd();
    setCostTrackingMode('same');
    setIndividualCosts([]);
    setIndividualDates([]);
    onOpenChange(false);
  };

  const onSubmit = async (data: ItemFormData) => {
    if (!user) {
      toast.error('Please sign in');
      return;
    }

    // Calculate per-item cost basis (handles total mode conversion and individual costs)
    const getPerItemCostBasisCents = (): number | null | undefined => {
      // If using individual costs mode, calculate average
      if (costTrackingMode === 'individual' && individualCosts.length > 0) {
        const validCosts = individualCosts
          .map(c => parseFloat(c))
          .filter(c => !isNaN(c) && c > 0);
        if (validCosts.length === 0) return isEditMode ? null : undefined;
        const averageCents = Math.round((validCosts.reduce((sum, c) => sum + c, 0) / validCosts.length) * 100);
        return averageCents;
      }
      
      // Standard single cost mode
      if (!data.cost_basis || Number(data.cost_basis) <= 0) return isEditMode ? null : undefined;
      const inputCents = Math.round(Number(data.cost_basis) * 100);
      // If in "total" mode and quantity > 1, divide to get per-item cost
      if (costBasisMode === 'total' && mode === 'item' && data.quantity > 1) {
        return Math.round(inputCents / data.quantity);
      }
      return inputCents;
    };
    
    // Get individual costs array for storage
    const getIndividualCostsCents = (): number[] | null | undefined => {
      if (costTrackingMode !== 'individual') return isEditMode ? null : undefined;
      const costs = individualCosts
        .map(c => parseFloat(c))
        .filter(c => !isNaN(c) && c > 0)
        .map(c => Math.round(c * 100));
      if (costs.length === 0) return isEditMode ? null : undefined;
      return costs;
    };
    
    // Get individual dates array for storage
    const getIndividualDatesArray = (): string[] | null | undefined => {
      if (costTrackingMode !== 'individual') return isEditMode ? null : undefined;
      // Only store dates if at least one is provided
      const hasDates = individualDates.some(d => d && d.trim() !== '');
      if (!hasDates) return isEditMode ? null : undefined;
      return individualDates;
    };

    // Determine condition
    let condition: string;
    if (mode === 'lot') {
      condition = data.lot_condition || 'lightly_played';
    } else if (data.category === 'singles') {
      condition = data.singles_condition || 'mint';
    } else if (data.category === 'accessories') {
      condition = data.accessory_condition || 'new';
    } else {
      condition = 'mint';
    }

    // Validate listing requirements
    if (data.is_listed) {
      // Price is only required if NOT open to offers
      const hasPrice = data.asking_price && Number(data.asking_price) > 0;
      const openToOffers = data.is_negotiable;
      
      if (!hasPrice && !openToOffers) {
        toast.error('Please enter an asking price or enable "Open to Offers"');
        return;
      }
      
      // Location is optional — retail/single-seller mode ships from a single
      // shop, so we don't gate saving on a per-listing city/state. If the
      // profile has one it still gets written below for local-pickup filters.
    }

    // Photos are optional - no minimum requirement

    setIsSubmitting(true);
    try {
      if (isEditMode && item) {
        // === EDIT MODE ===
        // Delete marked images
        for (const imageId of imagesToDelete) {
          await deleteImage.mutateAsync(imageId);
        }

        // Upload new images
        if (newImages.length > 0) {
          await uploadImages.mutateAsync({ itemId: item.id, files: newImages });
        }

        // Persist reordered existing images
        if (photoOrderDirty) {
          const visible = existingImages.filter(img => !imagesToDelete.includes(img.id));
          await Promise.all(
            visible.map((img, idx) =>
              supabase.from('user_item_images').update({ display_order: idx }).eq('id', img.id)
            )
          );
        }

        // Upload new proof pack images if any
        let updatedProofImages = { ...existingProofImages };
        if (Object.keys(proofImages).length > 0) {
          const newProofUrls = await uploadProofImages.mutateAsync({
            itemId: item.id,
            proofImages,
          });
          updatedProofImages = { ...updatedProofImages, ...newProofUrls };
        }

        const finalHasProofPack = Object.keys(updatedProofImages).length >= 3;

        // Update item
        await updateItem.mutateAsync({
          id: item.id,
          title: data.title,
          category: data.category,
          condition,
          game: data.game || null,
          custom_game_name: data.game === 'other' ? (data.custom_game_name || null) : null,
          language: data.language,
          set_name: data.set_name || null,
          product_type: (data.category === 'accessories' ? data.accessory_type : data.product_type) as ScProductType || null,
          quantity: data.quantity,
          purchase_date: data.purchase_date || null,
          cost_basis_cents: getPerItemCostBasisCents(),
          individual_costs_cents: getIndividualCostsCents(),
          individual_dates: getIndividualDatesArray(),
          is_listed: data.is_listed,
          asking_price_cents: data.asking_price ? Math.round(Number(data.asking_price) * 100) : null,
          is_negotiable: data.is_negotiable,
          accepts_trades: data.accepts_trades,
          ships_anywhere: profileWillShip,
          description: data.description || null,
          city: profileLocation?.city || null,
          state_code: profileLocation?.stateCode || null,
          // latitude/longitude removed from user_items for privacy

          target_price_cents: data.target_price ? Math.round(Number(data.target_price) * 100) : null,
          slab_grade: data.slab_grade || null,
          grading_company: data.grading_company === 'other' ? data.custom_grading_company : data.grading_company || null,
          certification_number: data.certification_number || null,
          sealed_condition: data.category === 'sealed' ? data.sealed_condition : null,
          card_number: normalizeCardNumber(data.card_number) || null,
          rarity: data.rarity || null,
          finish: data.finish || null,
          finish_label: data.finish_label || null,
          is_first_edition: data.is_first_edition,
          is_error_card: data.is_error_card,
          error_type: data.is_error_card ? data.error_type : null,
          visibility: data.is_listed ? 'listed' : 'private',
          proof_images: Object.keys(updatedProofImages).length > 0 ? updatedProofImages : null,
          has_proof_pack: finalHasProofPack,
          stock_image_url: stockImageUrl || null,
        });

        toast.success('Item updated successfully!');
      } else {
        // === ADD MODE ===
        // Standard single item creation (keeps multi-cost items together with individual_costs_cents array)
        const newItem = await createItem.mutateAsync({
            title: data.title,
            category: mode === 'lot' ? 'lots' : data.category,
            condition,
            game: mode === 'lot' ? undefined : data.game,
            custom_game_name: mode === 'lot' ? undefined : (data.game === 'other' ? data.custom_game_name : undefined),
            language: mode === 'lot' ? undefined : data.language,
            set_name: mode === 'lot' ? undefined : data.set_name,
            product_type: mode === 'lot' ? undefined : (data.category === 'accessories' ? data.accessory_type : data.product_type) as ScProductType | undefined,
            quantity: mode === 'lot' ? 1 : data.quantity,
            purchase_date: data.purchase_date,
            cost_basis_cents: getPerItemCostBasisCents(),
            individual_costs_cents: getIndividualCostsCents(),
            individual_dates: getIndividualDatesArray(),
            is_listed: data.is_listed,
            asking_price_cents: data.asking_price ? Math.round(Number(data.asking_price) * 100) : undefined,
            is_negotiable: data.is_negotiable,
            accepts_trades: data.accepts_trades,
            ships_anywhere: profileWillShip,
            description: data.description,
            city: profileLocation?.city,
            state_code: profileLocation?.stateCode,
            // latitude/longitude removed from user_items for privacy

            target_price_cents: data.target_price ? Math.round(Number(data.target_price) * 100) : undefined,
            slab_grade: mode === 'lot' ? undefined : data.slab_grade,
            grading_company: mode === 'lot' ? undefined : (data.grading_company === 'other' ? data.custom_grading_company : data.grading_company),
            certification_number: mode === 'lot' ? undefined : data.certification_number,
            sealed_condition: data.category === 'sealed' ? data.sealed_condition : undefined,
            card_number: mode === 'lot' ? undefined : normalizeCardNumber(data.card_number),
            rarity: mode === 'lot' ? undefined : data.rarity,
            finish: mode === 'lot' ? undefined : data.finish,
            finish_label: mode === 'lot' ? undefined : (data.finish_label ?? undefined),
            is_first_edition: mode === 'lot' ? undefined : data.is_first_edition,
            is_error_card: mode === 'lot' ? undefined : data.is_error_card,
            error_type: mode === 'lot' ? undefined : (data.is_error_card ? data.error_type : undefined),
            
            has_proof_pack: false,
            stock_image_url: stockImageUrl || undefined,
            // Lot-specific fields
            lot_type: mode === 'lot' ? data.lot_type : undefined,
            approx_card_count: mode === 'lot' ? data.approx_card_count : undefined,
            box_count: mode === 'lot' ? data.box_count : undefined,
            lot_highlights: mode === 'lot' ? data.lot_highlights : undefined,
            lot_eras: mode === 'lot' && data.lot_eras?.length ? data.lot_eras : undefined,
            lot_languages: mode === 'lot' && data.lot_languages?.length ? data.lot_languages : undefined,
            includes_holos: mode === 'lot' ? data.includes_holos : undefined,
            includes_ultras: mode === 'lot' ? data.includes_ultras : undefined,
            includes_slabs: mode === 'lot' ? data.includes_slabs : undefined,
          });

        if (newImages.length > 0) {
          await uploadImages.mutateAsync({ itemId: newItem.id, files: newImages });
        }

        // Inline proof of possession (Add mode) — upload + patch onto the newly created item
        if (Object.keys(proofImages).length > 0) {
          try {
            const newProofUrls = await uploadProofImages.mutateAsync({
              itemId: newItem.id,
              proofImages,
            });
            await updateItem.mutateAsync({
              id: newItem.id,
              proof_images: newProofUrls,
              has_proof_pack: Object.keys(newProofUrls).length >= 3,
            });
          } catch (err) {
            console.error('Proof upload failed (item still created):', err);
          }
        }

        toast.success(data.is_listed ? 'Item added and listed for sale!' : 'Item added to your inventory!');
      }

      // Final invalidation — deferred until after image + proof uploads land
      // so inventory never flashes an "Add Photo" placeholder for a brand-new
      // item whose gallery rows arrived a tick later.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.userItems.mineAll }),
        queryClient.invalidateQueries({ queryKey: queryKeys.marketplaceItems.all }),
      ]);

      resetAndClose();

    } catch (error) {
      console.error('Error saving item:', error);
      toast.error(isEditMode ? 'Failed to update item' : 'Failed to add item');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle button style helper
  const toggleBtnClass = (active: boolean, accent: 'gold' | 'primary' = 'gold') => cn(
    "px-3 py-1.5 rounded-full text-xs font-medium transition-all border",
    active 
      ? accent === 'gold'
        ? "bg-[hsl(44,100%,65%)/0.15] border-[hsl(44,100%,65%)/0.4] text-[hsl(44,100%,65%)] shadow-[0_0_8px_hsl(44,100%,65%,0.15)]"
        : "bg-primary/15 border-primary/40 text-primary shadow-[0_0_8px_rgba(0,165,223,0.15)]"
      : "bg-black/20 backdrop-blur-md shadow-[inset_0_1px_1px_rgba(255,255,255,0.03)] border-white/[0.08] text-white/50 hover:text-white/70"
  );

  // Header chrome — same JSX in both shells; mobile uses plain divs, desktop wraps title in DialogTitle.
  const TitleEl: any = isMobile ? 'div' : DialogTitle;
  const headerContent = isEditMode && item ? (
    <div className="flex items-center gap-3 text-left min-w-0">
      <div className="w-11 h-11 rounded-lg bg-white/[0.06] border border-white/[0.06] overflow-hidden shrink-0">
        {(item.images?.[0]?.image_url || (item as any).stock_image_url) ? (
          <img
            src={item.images?.[0]?.image_url || (item as any).stock_image_url}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <TitleEl className="text-[15px] font-semibold leading-tight truncate">
          {form.watch('title') || item.title || 'Edit Item'}
        </TitleEl>
        <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground/80">
          <span className="capitalize">{(CATEGORY_LABELS as any)[item.category] || item.category}</span>
          {(form.watch('set_name') || item.set_code) && <><span className="opacity-40">·</span><span className="truncate">{form.watch('set_name') || item.set_code}</span></>}
        </div>
      </div>
    </div>
  ) : (
    <TitleEl className="text-base font-semibold truncate">{mode === 'lot' ? 'Add Lot' : 'Add Item'}</TitleEl>
  );

  // (Step / progress bar removed — Add Item is one cohesive form.)

  // Shared form body — rendered once into whichever shell is active.
  const formBody = !user ? (
    <div className="text-center py-8">
      <p className="text-white/70 mb-4">Please sign in to {isEditMode ? 'edit' : 'add'} items</p>
      <Button onClick={resetAndClose}>Close</Button>
    </div>
  ) : (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0 h-full">
        <div className={cn("flex-1 overflow-y-auto px-5 pt-0 pb-4", isEditMode ? "flex flex-col gap-4" : "space-y-4")}>

                {/* ============================================= */}
                {/* STEP 1: What is it? (Identity) */}
                {/* In edit mode we render BOTH steps in one scroll. */}
                {/* ============================================= */}
                {/* ============================================= */}
                {/* FAST ADD ROW — compact path, default in Add mode */}
                {/* ============================================= */}
                {!isEditMode && mode === 'item' && (
                  <div
                    className={cn(
                      "rounded-lg transition-colors relative",
                      isDraggingPhotos && "ring-2 ring-active-gold/70 ring-offset-2 ring-offset-background bg-active-gold/[0.04]"
                    )}
                    onDragEnter={handlePhotosDragEnter}
                    onDragOver={handlePhotosDragOver}
                    onDragLeave={handlePhotosDragLeave}
                    onDrop={handlePhotosDrop}
                  >
                    {isDraggingPhotos && (
                      <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/70 backdrop-blur-sm">
                        <div className="flex items-center gap-2 text-sm font-medium text-active-gold">
                          <Upload className="w-4 h-4" />
                          Drop photos to add
                        </div>
                      </div>
                    )}
                    <FastAddRow
                      form={form}
                      newImagePreviews={newImagePreviews}
                      setNewImages={setNewImages}
                      setNewImagePreviews={setNewImagePreviews}
                      onContinueEditing={expanded ? undefined : () => setExpanded(true)}
                      expanded={expanded}
                      addedCount={0}
                      stockImageUrl={stockImageUrl}
                      setStockImageUrl={setStockImageUrl}
                      onOpenStockPicker={() => setStockImageDialogOpen(true)}
                      totalImageCount={totalImageCount}
                      orderableImages={orderableImages}
                      onAddFiles={addRawFiles}
                      onRemoveUploaded={handleRemoveUploadedImage}
                      onRemoveStock={handleRemoveStockImage}
                    />
                  </div>
                )}

                {(isEditMode || expanded || mode === 'lot') && (
                  <div className={cn("animate-fade-in", isEditMode ? "space-y-4 order-1" : "space-y-5")}>
                    {/* Single Item Identity Fields
                        In Add mode, Game / Title / Category live in the FastAddRow
                        above this section, so we skip them here to avoid duplication. */}
                    {mode === 'item' && (
                      <>
                        {isEditMode && (
                          <FormField control={form.control} name="game" render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <FloatingSelectField label="Game" hasValue={Boolean(field.value)}>
                                  <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="floating-trigger h-14"><SelectValue placeholder=" " /></SelectTrigger>
                                    <SelectContent>
                                      {TRADING_CARD_GAMES.map(game => (
                                        <SelectItem key={game.value} value={game.value}>
                                          {game.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </FloatingSelectField>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        )}

                        {isEditMode && (
                          <FormField control={form.control} name="title" render={({ field }) => {
                            const { hasDuplicates, potentialDuplicates } = useDuplicateDetection({
                              title: field.value || '',
                              category: watchCategory || '',
                              cardNumber: form.watch('card_number'),
                              excludeItemId: item?.id,
                            });
                            return (
                              <FormItem>
                                <FormControl>
                                  <FloatingInput label="Title" placeholder="e.g. Charizard VMAX Rainbow Rare" {...field} />
                                </FormControl>
                                <FormMessage />
                                {hasDuplicates && !isEditMode && (
                                  <div className="flex items-center gap-2 p-2 rounded-md bg-warning/10 border border-warning/20 text-sm mt-2">
                                    <AlertTriangle className="w-4 h-4 text-warning shrink-0" />
                                    <span className="text-warning/80/80">
                                      Similar item exists: "{potentialDuplicates[0]?.title}"
                                    </span>
                                  </div>
                                )}
                              </FormItem>
                            );
                          }} />
                        )}

                        {(watchCategory === 'singles' || watchCategory === 'slabs' || watchCategory === 'sealed') && (
                          <FormField control={form.control} name="set_name" render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <FloatingInput
                                  label="Set Name"
                                  placeholder={watchCategory === 'sealed' ? 'e.g. Scarlet & Violet, Prismatic Evolutions' : 'e.g. Vivid Voltage'}
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        )}

                        {watchGame === 'other' && (
                          <FormField control={form.control} name="custom_game_name" render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <FloatingInput
                                  label="Game Name"
                                  placeholder="e.g. Sorcery TCG"
                                  maxLength={50}
                                  {...field}
                                  value={field.value || ''}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        )}

                        {isEditMode && (
                          <FormField control={form.control} name="category" render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <FloatingSelectField label="Category" hasValue={Boolean(field.value)}>
                                  <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="floating-trigger h-14"><SelectValue placeholder=" " /></SelectTrigger>
                                    <SelectContent>
                                      {(Object.entries(CATEGORY_LABELS) as [ListingCategory, string][])
                                        .filter(([v]) => v !== 'lots')
                                        .map(([v, l]) => (
                                          <SelectItem key={v} value={v}>{l}</SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                </FloatingSelectField>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        )}
                      </>
                    )}

                    {/* Lot Identity Fields */}
                    {mode === 'lot' && (
                      <>
                        {/* Photos — moved to top of Lot form for parity with Add Item */}
                        {!isEditMode && (() => {
                          const heroImage = orderableImages[0];
                          const hasHero = Boolean(heroImage);
                          const canAddMore = totalImageCount < 10;
                          return (
                            <div
                              className={cn(
                                "space-y-4 rounded-lg transition-colors relative",
                                isDraggingPhotos && "ring-2 ring-active-gold/70 ring-offset-2 ring-offset-background bg-active-gold/[0.04]"
                              )}
                              onDragEnter={handlePhotosDragEnter}
                              onDragOver={handlePhotosDragOver}
                              onDragLeave={handlePhotosDragLeave}
                              onDrop={handlePhotosDrop}
                            >
                              {isDraggingPhotos && (
                                <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/70 backdrop-blur-sm">
                                  <div className="flex items-center gap-2 text-sm font-medium text-active-gold">
                                    <Upload className="w-4 h-4" />
                                    Drop photos to add
                                  </div>
                                </div>
                              )}

                              <input
                                ref={lotHeroFileRef}
                                type="file"
                                accept="image/*,.heic,.heif,.dng"
                                multiple
                                className="hidden"
                                onChange={handleImageChange}
                              />

                              <div
                                className={cn(
                                  "relative w-full rounded-2xl overflow-hidden field-surface field-border flex",
                                  hasHero ? "aspect-[4/3] sm:aspect-auto sm:h-44" : "h-20 sm:h-24"
                                )}
                              >
                                {hasHero ? (
                                  <>
                                    <img src={heroImage.url} alt="" className="absolute inset-0 w-full h-full object-contain" />
                                    {heroImage.type === 'stock' && (
                                      <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/70 text-[10px] font-medium text-white/90 uppercase tracking-wide">
                                        Stock photo
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (heroImage.type === 'stock') {
                                          handleRemoveStockImage();
                                        } else {
                                          handleRemoveUploadedImage(heroImage.index);
                                        }
                                      }}
                                      className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70 hover:bg-black/85"
                                      aria-label="Remove photo"
                                    >
                                      <X className="w-4 h-4 text-white" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => lotHeroFileRef.current?.click()}
                                      className="flex-1 flex flex-row items-center justify-center gap-3 px-3 hover:bg-white/[0.03] transition-colors"
                                    >
                                      <Camera className="w-5 h-5 text-muted-foreground shrink-0" strokeWidth={1.75} />
                                      <div className="flex flex-col items-start min-w-0">
                                        <span className="text-sm font-medium text-foreground/85 leading-tight">Add photos</span>
                                        <span className="text-[11px] text-muted-foreground/70 leading-tight">Capture or choose</span>
                                      </div>
                                    </button>

                                    <div className="relative w-px flex flex-col items-center justify-center py-3">
                                      <div className="flex-1 w-px bg-white/[0.06]" />
                                      <span className="my-1.5 text-[9px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                                        or
                                      </span>
                                      <div className="flex-1 w-px bg-white/[0.06]" />
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() => setStockImageDialogOpen(true)}
                                      className="flex-1 flex flex-row items-center justify-center gap-3 px-3 hover:bg-white/[0.03] transition-colors"
                                    >
                                      <Images className="w-5 h-5 text-muted-foreground shrink-0" strokeWidth={1.75} />
                                      <div className="flex flex-col items-start min-w-0">
                                        <span className="text-sm font-medium text-foreground/85 leading-tight">Stock photo</span>
                                        <span className="text-[11px] text-muted-foreground/70 leading-tight">Search library</span>
                                      </div>
                                    </button>
                                  </>
                                )}
                              </div>

                              {orderableImages.length > 1 && (
                                <div className="flex gap-2 overflow-x-auto pb-1">
                                  {orderableImages.map((img, orderIdx) => (
                                    <div
                                      key={img.type === 'stock' ? 'stock' : `new-${img.index}`}
                                      data-image-index={orderIdx}
                                      draggable={orderableImages.length > 1}
                                      onDragStart={() => orderableImages.length > 1 && handleDragStart(orderIdx)}
                                      onDragOver={(e) => orderableImages.length > 1 && handleDragOver(e, orderIdx)}
                                      onDragEnd={handleDragEnd}
                                      onTouchStart={(e) => orderableImages.length > 1 && handleTouchStart(orderIdx, e)}
                                      onTouchMove={handleTouchMove}
                                      onTouchEnd={handleTouchEnd}
                                      className={cn(
                                        "relative w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden field-surface field-border transition-all select-none cursor-grab",
                                        (dragIndex === orderIdx || touchDragIndex === orderIdx) && "opacity-50 scale-95 ring-2 ring-primary/50"
                                      )}
                                    >
                                      <img src={img.url} alt="" className="w-full h-full object-cover pointer-events-none" draggable={false} />
                                      {img.type === 'stock' && (
                                        <span className="absolute bottom-0.5 left-0.5 px-1 py-0 text-[8px] font-medium uppercase tracking-wide bg-black/70 text-white/85 rounded">
                                          Stock
                                        </span>
                                      )}
                                      {orderIdx === 0 && touchDragIndex === null && dragIndex === null && (
                                        <span className="absolute top-0.5 left-0.5 px-1 py-0 text-[8px] font-medium bg-primary text-primary-foreground rounded">
                                          Cover
                                        </span>
                                      )}
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (img.type === 'stock') {
                                            handleRemoveStockImage();
                                          } else {
                                            handleRemoveUploadedImage(img.index);
                                          }
                                        }}
                                        onTouchStart={(e) => e.stopPropagation()}
                                        className="absolute top-0.5 right-0.5 p-0.5 bg-black/70 rounded-full hover:bg-black/90 transition-colors"
                                        aria-label="Remove photo"
                                      >
                                        <X className="w-3 h-3 text-white" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {hasHero && canAddMore && (
                                <div className="grid grid-cols-3 gap-2">
                                  <label className="h-14 rounded-2xl field-surface field-border flex items-center justify-center gap-2 text-sm font-medium text-foreground/80 hover:bg-white/[0.03] cursor-pointer transition-colors">
                                    <Camera className="w-4 h-4 text-muted-foreground" />
                                    Take
                                    <input
                                      ref={cameraInputRef}
                                      type="file"
                                      accept="image/*,.heic,.heif,.dng"
                                      capture="environment"
                                      onChange={handleImageChange}
                                      className="sr-only"
                                      aria-hidden="true"
                                    />
                                  </label>
                                  <label className="h-14 rounded-2xl field-surface field-border flex items-center justify-center gap-2 text-sm font-medium text-foreground/80 hover:bg-white/[0.03] cursor-pointer transition-colors">
                                    <Upload className="w-4 h-4 text-muted-foreground" />
                                    Upload
                                    <input type="file" accept="image/*,.heic,.heif,.dng" multiple onChange={handleImageChange} className="hidden" />
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => setStockImageDialogOpen(true)}
                                    disabled={Boolean(stockImageUrl)}
                                    className="h-14 rounded-2xl field-surface field-border flex items-center justify-center gap-2 text-sm font-medium text-foreground/80 hover:bg-white/[0.03] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    <Images className="w-4 h-4 text-muted-foreground" />
                                    Sample
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        <FormField control={form.control} name="title" render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <FloatingInput
                                label="Title"
                                placeholder={
                                  watchLotType === 'singles_lot' 
                                    ? "e.g. 100+ Cards Mixed Bulk Lot"
                                    : watchLotType === 'sealed_lot'
                                      ? "e.g. ETB + 3 Booster Box Collection"
                                      : "e.g. Complete Trainer Gallery + Sealed Promos"
                                } 
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />

                        <div className="space-y-2">
                          <h4 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground/90 mb-3">
                            Lot Type
                          </h4>
                          <div className="grid grid-cols-3 gap-2">
                            {(['singles_lot', 'sealed_lot', 'mixed_lot'] as LotType[]).map((type) => (
                              <button
                                key={type}
                                type="button"
                                onClick={() => form.setValue('lot_type', type)}
                                className={cn(
                                  "py-3 px-3 rounded-xl text-sm font-medium transition-all duration-200 border flex flex-col items-center gap-1.5",
                                  watchLotType === type
                                    ? "bg-active-gold/15 text-active-gold border-active-gold/50 shadow-[0_0_0_1px_hsl(var(--active-gold)/0.15)]"
                                    : "bg-white/[0.05] text-foreground/80 border-white/[0.06] hover:bg-white/[0.08] hover:text-foreground"
                                )}
                              >
                                {type === 'singles_lot' && <span className="text-lg">🃏</span>}
                                {type === 'sealed_lot' && <span className="text-lg">📦</span>}
                                {type === 'mixed_lot' && <span className="text-lg">🃏+📦</span>}
                                <span className="text-xs">{LOT_TYPE_LABELS[type]}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Investment Tracking moved to bottom of form */}
                  </div>
                )}

                {/* ============================================= */}
                {/* STEP 2: Details & Listing */}
                {/* ============================================= */}
                {(isEditMode || expanded) && (
                  <div className={cn("animate-fade-in", isEditMode ? "space-y-4 order-2" : "space-y-5")}>
                    {/* LOT MODE - Step 2 */}
                    {mode === 'lot' && (
                      <>
                        <div className="flex flex-col gap-4">
                          {(watchLotType === 'singles_lot' || watchLotType === 'mixed_lot') && (
                            <FormField control={form.control} name="approx_card_count" render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <FloatingInput
                                    label="Approx. Card Count"
                                    type="number" 
                                    min={1} 
                                    placeholder="e.g. 500" 
                                    value={field.value || ''} 
                                    onChange={(e) => field.onChange(parseInt(e.target.value) || undefined)} 
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )} />
                          )}
                          {(watchLotType === 'sealed_lot' || watchLotType === 'mixed_lot') && (
                            <FormField control={form.control} name="box_count" render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <FloatingInput
                                    label="Sealed Products"
                                    type="text"
                                    floating={false}
                                    placeholder="Sealed Products (Ex. 1 Destined Rivals BB, 1 Prismatic Evolutions Bundle...)"
                                    value={field.value || ''}
                                    onChange={(e) => field.onChange(e.target.value || undefined)}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )} />
                          )}
                        </div>

                        <FormField control={form.control} name="lot_condition" render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <FloatingSelectField label="Overall Condition" hasValue>
                                <Select onValueChange={field.onChange} value={field.value || 'lightly_played'}>
                                  <SelectTrigger className="floating-trigger h-14"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {(Object.entries(SINGLES_CONDITION_LABELS) as [SinglesCondition, string][]).map(([v, l]) => (
                                      <SelectItem key={v} value={v}>{l}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </FloatingSelectField>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />

                        <LanguageMultiSelectField
                          value={watchLotLanguages}
                          onChange={(langs) => form.setValue('lot_languages', langs)}
                        />


                        <FormField control={form.control} name="description" render={({ field }) => (
                          <FormItem className="space-y-1.5">
                            <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/80 px-0.5">
                              Notes / Description
                            </label>
                            <FormControl>
                              <Textarea
                                placeholder="Highlights, standout cards, what's included…"
                                rows={3}
                                className="field-surface field-border placeholder:text-muted-foreground/50"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />
                      </>
                    )}

                    {/* ITEM MODE - Step 2 */}
                    {mode === 'item' && (
                      <>

                        {watchCategory === 'accessories' && (
                          <FormField control={form.control} name="accessory_type" render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <FloatingSelectField label="Accessory Type" hasValue={Boolean(field.value)}>
                                  <Select onValueChange={field.onChange} value={field.value || ''}>
                                    <SelectTrigger className="floating-trigger h-14"><SelectValue placeholder=" " /></SelectTrigger>
                                    <SelectContent>
                                      {(Object.entries(ACCESSORY_TYPE_LABELS) as [AccessoryType, string][]).map(([v, l]) => (
                                        <SelectItem key={v} value={v}>{l}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </FloatingSelectField>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        )}

                        {watchCategory === 'sealed' && (
                          <FormField control={form.control} name="product_type" render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <FloatingSelectField label="Product Type" hasValue>
                                  <Select onValueChange={field.onChange} value={field.value || ''}>
                                    <SelectTrigger className="floating-trigger h-14"><SelectValue placeholder="Select…" /></SelectTrigger>
                                    <SelectContent>
                                      {PRODUCT_TYPE_OPTIONS.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value} className="text-base py-3">{opt.label}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </FloatingSelectField>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        )}

                        {/* Slab-specific fields */}
                        {watchCategory === 'slabs' && (
                          <div className={cn(
                            "space-y-4",
                            isEditMode
                              ? ""
                              : "p-4 rounded-lg border border-white/[0.08] bg-chrome sm:bg-foreground/[0.03]"
                          )}>
                            <FormField control={form.control} name="grading_company" render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <FloatingSelectField label="Grading Company" hasValue={Boolean(field.value)}>
                                    <GradingCompanyCombobox
                                      value={field.value || ''}
                                      onChange={field.onChange}
                                      customName={form.watch('custom_grading_company') || ''}
                                      onCustomNameChange={(val) => form.setValue('custom_grading_company', val)}
                                      placeholder=" "
                                      className="floating-trigger h-14 pt-5"
                                    />
                                  </FloatingSelectField>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )} />

                            <div className="grid grid-cols-2 gap-4 items-start">
                              <FormField control={form.control} name="slab_grade" render={({ field }) => (
                                <FormItem className="min-w-0">
                                  <FormControl>
                                    <FloatingInput
                                      label="Grade"
                                      type="number"
                                      inputMode="decimal"
                                      min={1}
                                      max={10}
                                      step={supportsHalfGrades ? 0.5 : 1}
                                      placeholder="1-10"
                                      value={field.value ?? ''}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        if (!v) { field.onChange(undefined); return; }
                                        let n = parseFloat(v);
                                        if (isNaN(n)) return;
                                        n = Math.max(1, Math.min(10, n));
                                        n = supportsHalfGrades ? Math.round(n * 2) / 2 : Math.round(n);
                                        field.onChange(n);
                                      }}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )} />

                              <FormField control={form.control} name="certification_number" render={({ field }) => (
                                <FormItem className="min-w-0">
                                  <FormControl>
                                    <FloatingInput label="Certification #" placeholder="e.g. 12345678" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )} />
                            </div>

                            {/* Premium grade callout (e.g., Black Label / Pristine) */}
                            {form.watch('slab_grade') === 10 && premiumGradeLabel && (
                              <label className="flex items-center gap-2 p-3 rounded-lg border border-[hsl(50,100%,68%)/0.3] bg-[hsl(50,100%,68%)/0.1] cursor-pointer">
                                <Checkbox
                                  checked={watchIsPremiumGrade}
                                  onCheckedChange={(checked) => form.setValue('is_premium_grade', !!checked)}
                                />
                                <span className="text-sm">
                                  This is a <span className="font-semibold text-active-gold">{premiumGradeLabel} 10</span>
                                  <span className="text-muted-foreground ml-1">(e.g., BGS Black Label, CGC Pristine)</span>
                                </span>
                              </label>
                            )}

                            <div className="grid grid-cols-2 gap-4 items-start">
                              <FormField control={form.control} name="quantity" render={({ field }) => (
                                <FormItem className="min-w-0">
                                  <FormControl>
                                    <FloatingInput label="Quantity" type="number" min={1} {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )} />

                              <FormField control={form.control} name="language" render={({ field }) => (
                                <FormItem className="min-w-0">
                                  <FormControl>
                                    <FloatingSelectField label="Language" hasValue={Boolean(field.value)}>
                                      <Select onValueChange={field.onChange} value={field.value}>
                                        <SelectTrigger className="floating-trigger h-14"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          {LANGUAGE_OPTIONS.map(lang => (
                                            <SelectItem key={lang.value} value={lang.value}>
                                              {lang.flag} {lang.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </FloatingSelectField>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )} />
                            </div>
                          </div>
                        )}

                        {/* Singles Card Metadata */}
                        {watchCategory === 'singles' && (
                          <div className={cn(
                            "space-y-4",
                            isEditMode
                              ? ""
                              : "p-4 rounded-lg border border-white/[0.08] bg-chrome sm:bg-foreground/[0.03]"
                          )}>
                            
                            <div className="grid grid-cols-2 gap-3">
                              <FormField control={form.control} name="card_number" render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <FloatingInput label="Card Number" placeholder="e.g. 4/102" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )} />
                              
                              <FormField control={form.control} name="finish" render={({ field }) => {
                                const labelField = form.watch('finish_label');
                                return (
                                  <FormItem className="min-w-0">
                                    <FormControl>
                                      <FinishCombobox
                                        value={{ finish: (field.value as any) || '', finish_label: labelField ?? null }}
                                        onChange={(next) => {
                                          field.onChange(next.finish || '');
                                          form.setValue('finish_label', next.finish_label, { shouldDirty: true });
                                        }}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                );
                              }} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <FormField control={form.control} name="is_first_edition" render={({ field }) => (
                                <FormItem className="flex-1 min-w-[140px]">
                                  <label className="flex items-center gap-3 h-14 px-3 rounded-lg border border-white/[0.08] bg-white/[0.03] active:bg-white/[0.06] cursor-pointer select-none">
                                    <FormControl>
                                      <Checkbox checked={field.value} onCheckedChange={field.onChange} className="h-5 w-5" />
                                    </FormControl>
                                    <FormLabel className="!mt-0 cursor-pointer text-sm">1st Edition</FormLabel>
                                  </label>
                                </FormItem>
                              )} />

                              <FormField control={form.control} name="is_error_card" render={({ field }) => (
                                <FormItem className="flex-1 min-w-[140px]">
                                  <label className="flex items-center gap-3 h-14 px-3 rounded-lg border border-white/[0.08] bg-white/[0.03] active:bg-white/[0.06] cursor-pointer select-none">
                                    <FormControl>
                                      <Checkbox checked={field.value} onCheckedChange={field.onChange} className="h-5 w-5" />
                                    </FormControl>
                                    <FormLabel className="!mt-0 cursor-pointer text-sm">Error/Misprint</FormLabel>
                                  </label>
                                </FormItem>
                              )} />
                            </div>

                            {watchIsErrorCard && (
                              <FormField control={form.control} name="error_type" render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <FloatingSelectField label="Error Type" hasValue={Boolean(field.value)}>
                                      <Select onValueChange={field.onChange} value={field.value || ''}>
                                        <SelectTrigger className="floating-trigger h-14"><SelectValue placeholder=" " /></SelectTrigger>
                                        <SelectContent>
                                          {ERROR_TYPE_OPTIONS.map(err => (
                                            <SelectItem key={err.value} value={err.value}>{err.label}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </FloatingSelectField>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )} />
                            )}
                          </div>
                        )}

                        {/* Set Name moved to top-level identity block */}

                        {/* Specifics: Condition + Language + Quantity (slabs render their own above) */}
                        {watchCategory !== 'slabs' && (
                        <div className="grid grid-cols-2 gap-4 items-start">
                          {watchCategory === 'singles' && (
                            <FormField control={form.control} name="singles_condition" render={({ field }) => (
                              <FormItem className="min-w-0">
                                <FormControl>
                                  <FloatingSelectField label="Condition" hasValue={Boolean(field.value || 'mint')}>
                                    <Select onValueChange={field.onChange} value={field.value || 'mint'}>
                                      <SelectTrigger className="floating-trigger h-14"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        {(Object.entries(SINGLES_CONDITION_LABELS) as [SinglesCondition, string][]).map(([v, l]) => (
                                          <SelectItem key={v} value={v}>{l}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </FloatingSelectField>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )} />
                          )}

                          {watchCategory === 'sealed' && (
                            <FormField control={form.control} name="sealed_condition" render={({ field }) => (
                              <FormItem className="min-w-0">
                                <FormControl>
                                  <FloatingSelectField label="Condition" hasValue={Boolean(field.value || 'factory_sealed')}>
                                    <Select onValueChange={field.onChange} value={field.value || 'factory_sealed'}>
                                      <SelectTrigger className="floating-trigger h-14"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        {(Object.entries(SEALED_CONDITION_LABELS) as [SealedCondition, string][]).map(([v, l]) => (
                                          <SelectItem key={v} value={v}>{l}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </FloatingSelectField>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )} />
                          )}

                          {watchCategory === 'accessories' && (
                            <FormField control={form.control} name="accessory_condition" render={({ field }) => (
                              <FormItem className="min-w-0">
                                <FormControl>
                                  <FloatingSelectField label="Condition" hasValue={Boolean(field.value || 'new')}>
                                    <Select onValueChange={field.onChange} value={field.value || 'new'}>
                                      <SelectTrigger className="floating-trigger h-14"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        {(Object.entries(ACCESSORY_CONDITION_LABELS) as [AccessoryCondition, string][]).map(([v, l]) => (
                                          <SelectItem key={v} value={v}>{l}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </FloatingSelectField>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )} />
                          )}

                          <FormField control={form.control} name="language" render={({ field }) => (
                            <FormItem className="min-w-0">
                              <FormControl>
                                <FloatingSelectField label="Language" hasValue={Boolean(field.value)}>
                                  <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="floating-trigger h-14"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      {LANGUAGE_OPTIONS.map(lang => (
                                        <SelectItem key={lang.value} value={lang.value}>
                                          {lang.flag} {lang.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </FloatingSelectField>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        </div>
                        )}

                        {watchCategory !== 'slabs' && (
                          <FormField control={form.control} name="quantity" render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <FloatingInput label="Quantity" type="number" min={1} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        )}
                      </>
                    )}

                    {/* Photos for Lot mode now render in Step 1 above. For mode === 'item',
                        photos live in FastAddRow above. In edit mode, photos & proof live with
                        pricing in the Manage Item drawer. */}

                    {/* Proof of Possession — works in both add & edit modes for singles/slabs/sealed.
                        Rendered outside the lot-only Photos block so it appears in item mode too. */}
                    {mode === 'item' && (watchCategory === 'singles' || watchCategory === 'slabs' || watchCategory === 'sealed') && (
                      <Collapsible open={proofPackOpen} onOpenChange={setProofPackOpen} className={cn(
                        "overflow-hidden rounded-xl border transition-all",
                        hasProofPack
                          ? "border-active-gold/40 bg-gradient-to-br from-active-gold/[0.12] via-active-gold/[0.04] to-transparent"
                          : "border-active-gold/25 bg-gradient-to-br from-active-gold/[0.08] via-white/[0.02] to-transparent",
                        proofPackOpen && "border-active-gold/40"
                      )}>
                        <CollapsibleTrigger type="button" className={cn(
                          "group relative w-full text-left transition-all",
                          !hasProofPack && "hover:bg-active-gold/[0.04]",
                          "[&[data-state=open]>div>svg.chev]:rotate-180"
                        )}>
                          <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-active-gold/60 to-transparent" />
                          <div className="flex items-center gap-3 px-4 py-3">
                            <div className={cn(
                              "relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
                              hasProofPack
                                ? "bg-gradient-to-br from-active-gold/30 to-active-gold/10 ring-1 ring-active-gold/50 shadow-[0_0_12px_hsl(var(--active-gold)/0.35)]"
                                : "bg-gradient-to-br from-active-gold/20 to-active-gold/5 ring-1 ring-active-gold/30"
                            )}>
                              <Shield className="w-6 h-6 text-active-gold fill-active-gold/15" strokeWidth={2} />
                              <Check
                                className="absolute w-3.5 h-3.5 text-success drop-shadow-[0_0_4px_rgba(52,211,153,0.7)]"
                                strokeWidth={3.5}
                                style={{ top: '52%', left: '50%', transform: 'translate(-50%, -50%)' }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-foreground">Proof of Possession</span>
                                {hasProofPack ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-active-gold bg-active-gold/15 px-1.5 py-0.5 rounded-full">
                                    <Sparkles className="w-2.5 h-2.5" /> Verified
                                  </span>
                                ) : (
                                  <span className="text-[10px] font-medium uppercase tracking-wide text-active-gold/80 bg-active-gold/10 px-1.5 py-0.5 rounded-full">
                                    {proofImages.possession ? 'Captured' : 'Optional'}
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-muted-foreground/80 mt-0.5 leading-snug">
                                {hasProofPack
                                  ? "Earns a trust badge on your seller page and ranks this listing higher with buyers."
                                  : "Add a photo of your @handle + today's date next to the item."}
                              </p>
                            </div>
                            <ChevronDown className="chev w-4 h-4 text-muted-foreground transition-transform shrink-0" />
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="mx-4 mb-4 pt-4 border-t border-active-gold/20">
                            {Object.keys(existingProofImages).length > 0 && (
                              <div className="mb-4">
                                <ProofPackGallery
                                  proofImages={existingProofImages}
                                />
                                <p className="text-xs text-muted-foreground mt-2">Add new photos below to update</p>
                              </div>
                            )}
                            <ProofPackUpload
                              proofImages={proofImages}
                              setProofImages={setProofImages}
                              category={watchCategory as 'singles' | 'slabs' | 'sealed'}
                              username={profileUsername}
                              quantity={watchQuantity}
                            />
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}

                    {/* Description */}
                    {mode === 'item' && (
                    <FormField control={form.control} name="description" render={({ field }) => (
                      <FormItem className="space-y-1.5">
                        <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/80 px-0.5">
                          Notes / Description
                        </label>
                        <FormControl>
                          <Textarea
                            placeholder="Condition, flaws, history…"
                            rows={3}
                            className="field-surface field-border placeholder:text-muted-foreground/50"
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )} />
                    )}

                    {/* Photos, reorder, and Proof of Possession only render in add mode here.
                        For existing items they live in the Manage Item drawer alongside pricing. */}

                    {/* Visibility & Listing - only when adding new items.
                        For existing items, pricing/listing/offers live in the Manage Item sheet
                        (QuickPriceEditDialog) as the single source of truth. */}
                    {!isEditMode && (<>
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/80 px-0.5">Visibility</label>
                      <FormField control={form.control} name="is_listed" render={({ field }) => (
                        <FormItem>
                          <div className="flex gap-1 p-1 rounded-lg field-surface field-border">
                            <button
                              type="button"
                              onClick={() => field.onChange(false)}
                              className={cn(
                                "flex-1 py-2 px-4 rounded-md text-xs font-medium transition-all duration-200 border",
                                !field.value 
                                  ? "bg-gradient-to-r from-white/[0.12] to-white/[0.06] text-white border-white/30 shadow-[0_0_10px_rgba(255,255,255,0.06)]" 
                                  : "text-white/50 hover:text-white/70 border-transparent"
                              )}
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              Private
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (!hasAcceptedTerms && !isEditMode) {
                                  setShowTermsModal(true);
                                } else {
                                  field.onChange(true);
                                }
                              }}
                              className={cn(
                                "flex-1 py-2 px-4 rounded-md text-xs font-medium transition-all duration-200 border",
                                field.value 
                                  ? "bg-gradient-to-r from-white/[0.12] to-white/[0.06] border-white/30 text-white shadow-[0_0_10px_rgba(255,255,255,0.06)]" 
                                  : "text-white/50 hover:text-white/70 border-transparent"
                              )}
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              List for Sale
                            </button>
                          </div>
                        </FormItem>
                      )} />
                    </div>

                    {/* Open to Offers toggle */}
                    {(watchIsListed || watchAskingPrice) && (
                      <FormField control={form.control} name="is_negotiable" render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between h-14 px-3 rounded-lg field-surface field-border text-sm">
                            <div className="flex items-center gap-3">
                              <MessageCircle className="w-5 h-5 text-muted-foreground/50" />
                              <span className="text-muted-foreground/80">Open to Offers</span>
                            </div>
                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                          </div>
                        </FormItem>
                      )} />
                    )}

                    {/* Listing Details */}
                    {watchIsListed && (
                      <div className="space-y-4">
                        <FormField control={form.control} name="asking_price" render={({ field }) => (
                          <FormItem className="space-y-2">
                            <FormControl>
                              <div className="relative">
                                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-muted-foreground/60 z-10 flex items-center justify-center w-4">
                                  $
                                </span>
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  pattern="[0-9]*\.?[0-9]*"
                                  {...PRICE_INPUT_PROPS}
                                  placeholder="Asking Price"
                                  className="h-14 pl-9 text-base field-surface field-border placeholder:text-muted-foreground/60"
                                  {...field}
                                  onChange={(e) => field.onChange(e.target.value.replace(/[^0-9.]/g, ''))}
                                />
                              </div>
                            </FormControl>
                            {(() => {
                              const asking = parseFloat(watchAskingPrice || '0');
                              const cost = parseFloat(watchCostBasis || '0');
                              if (!asking || !cost) return null;
                              const profit = asking - cost;
                              const pct = ((profit / cost) * 100).toFixed(0);
                              return (
                                <FormDescription className={profit >= 0 ? 'text-success' : 'text-destructive'}>
                                  {profit >= 0 ? '+' : ''}{profit.toFixed(2)} ({pct}%) vs cost basis
                                </FormDescription>
                              );
                            })()}
                            {(() => {
                              if (!isEditMode || !item) return null;
                              const collectrCents = resolveCollectrPriceCents(item);
                              if (collectrCents === null) return null;
                              const currentCents = Math.round((parseFloat(watchAskingPrice || '0') || 0) * 100);
                              if (currentCents === collectrCents) return null;
                              const dollars = (collectrCents / 100).toFixed(2);
                              return (
                                <button
                                  type="button"
                                  onClick={() => form.setValue('asking_price', dollars, { shouldDirty: true, shouldValidate: true })}
                                  className="mt-1.5 text-xs text-muted-foreground hover:text-active-gold transition-colors text-left"
                                >
                                  Use Collectr import price: <span className="text-active-gold/90">{formatCents(collectrCents)}</span>
                                </button>
                              );
                            })()}
                            <FormMessage />
                          </FormItem>
                        )} />

                        {(() => {
                          const askingCents = Math.round((parseFloat(watchAskingPrice || '0') || 0) * 100);
                          if (askingCents < HIGH_VALUE_LISTING_CENTS) return null;
                          return (
                            <div className="flex gap-3 p-3 rounded-lg border border-caution/30 bg-caution/10">
                              <AlertTriangle className="h-4 w-4 text-caution shrink-0 mt-0.5" />
                              <div className="text-xs text-foreground/90 leading-relaxed">
                                <strong>High-value listing tip.</strong> Meet buyers at a card show, your local game store, or a police "safe exchange zone." Avoid private residences. CardSimp does not insure or mediate transactions —{' '}
                                <Link to="/safety" className="text-primary hover:underline">read meetup safety tips</Link>.
                              </div>
                            </div>
                          );
                        })()}

                        {totalImageCount === 0 && (
                          <p className="text-sm text-destructive">At least 1 photo required to list</p>
                        )}
                      </div>
                    )}
                    </>)}
                  </div>
                )}

                {/* ============================================= */}
                {/* Investment Tracking — pinned to bottom         */}
                {/* ============================================= */}
                {(isEditMode || expanded) && mode === 'item' && (
                  <InvestmentTrackingSection
                    form={form}
                    isEditMode={isEditMode}
                    investmentOpen={investmentOpen}
                    setInvestmentOpen={setInvestmentOpen}
                    watchQuantity={watchQuantity}
                    watchCostBasis={watchCostBasis}
                    costTrackingMode={costTrackingMode}
                    setCostTrackingMode={setCostTrackingMode}
                    costBasisMode={costBasisMode}
                    setCostBasisMode={setCostBasisMode}
                    individualCosts={individualCosts}
                    setIndividualCosts={setIndividualCosts}
                    individualDates={individualDates}
                    setIndividualDates={setIndividualDates}
                  />
                )}

                </div>
                {/* Sticky Footer */}
                <div
                  className="shrink-0 flex items-center gap-3 px-5 pt-3"
                  style={{
                    paddingBottom:
                      isMobile && keyboardHeight > 0
                        ? `${keyboardHeight + 12}px`
                        : 'max(env(safe-area-inset-bottom), 12px)',
                  }}
                >
                  {isEditMode ? (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteItemConfirmOpen(true)}
                        disabled={isSubmitting || isDeletingItem}
                        aria-label="Delete this item"
                        className="h-11 w-11 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                      <Button type="button" variant="outline" onClick={resetAndClose} className="flex-1 sm:flex-none sm:px-6">
                        Cancel
                      </Button>
                      {(() => {
                        const dirtyCount = Object.keys(form.formState.dirtyFields).length;
                        const isDirty = dirtyCount > 0;
                        return (
                          <Button
                            key="submit-button-edit"
                            type="submit"
                            disabled={isSubmitting || !isDirty}
                            className={cn('flex-1', goldGradientActive)}
                          >
                            {isSubmitting ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                            ) : isDirty ? (
                              'Save'
                            ) : (
                              'No changes'
                            )}
                          </Button>
                        );
                      })()}
                    </>
                  ) : (
                    <Button
                      key="submit-button"
                      type="submit"
                      disabled={isSubmitting}
                      className={cn('flex-1', goldGradientActive)}
                    >
                      {isSubmitting
                        ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding…</>
                        : (watchIsListed ? 'Add & List for Sale' : 'Add to Inventory')}
                    </Button>
                  )}
                </div>
              </form>
            </Form>
  );

  return (
    <>
      {isMobile ? (
        <MobileFullscreenPage
          open={open}
          onClose={resetAndClose}
          title={headerContent}
          bodyScroll={false}
        >
          {formBody}
        </MobileFullscreenPage>
      ) : (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && resetAndClose()}>
          <DialogContent className="p-0 gap-0 flex flex-col max-w-none w-screen h-[100dvh] max-h-[100dvh] inset-0 top-0 left-0 translate-x-0 translate-y-0 rounded-none border-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-2xl sm:w-[95vw] sm:h-auto sm:max-h-[90dvh] sm:rounded-lg sm:border">
            <DialogHeader className="px-5 pt-2 pb-1 shrink-0 text-left">
              {headerContent}
            </DialogHeader>
            {formBody}
          </DialogContent>
        </Dialog>
      )}

      {/* Image Editor Dialog */}
      <ItemImageEditorDialog
        open={editingImageIndex !== null}
        imageSrc={editingImageSrc}
        onClose={closeEditor}
        onSave={handleImageEdited}
      />

      {/* Terms Acceptance Modal */}
      <TermsAcceptanceModal
        open={showTermsModal}
        onOpenChange={setShowTermsModal}
        onAccepted={() => {
          refreshTermsStatus();
          form.setValue('is_listed', true);
        }}
      />

      {/* Delete Item Confirmation Dialog — independent of the form's Save flow */}
      {isEditMode && item && (
        <DeleteItemsConfirmDialog
          open={deleteItemConfirmOpen}
          onOpenChange={setDeleteItemConfirmOpen}
          count={1}
          itemTitle={item.title}
          isDeleting={isDeletingItem}
          onConfirm={handleDeleteThisItem}
        />
      )}

      {/* Confirm Delete Image Dialog */}
      <ConfirmDeleteImageDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        onConfirm={confirmRemoveImage}
      />

      {/* Stock Image Selection Dialog */}
      {stockImageDialogOpen && (
        <ImageSelectionDialog
          open={stockImageDialogOpen}
          onClose={() => setStockImageDialogOpen(false)}
          defaultTab="stock"
          stockOnly
          item={isEditMode && item ? item : {
            id: 'temp',
            user_id: user?.id || '',
            title: form.getValues('title') || '',
            category: form.getValues('category'),
            set_code: form.getValues('set_name') || null,
            set_name: form.getValues('set_name') || null,
            grading_company: form.getValues('grading_company') || null,
            slab_grade: form.getValues('slab_grade') || null,
            card_number: form.getValues('card_number') || null,
            game: form.getValues('game') || null,
            language: form.getValues('language') || null,
            rarity: form.getValues('rarity') || null,
            product_type: form.getValues('product_type') as any || null,
            visibility: 'private',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          } as UserItem}
          hasExistingImages={totalImageCount > 0}
          onStockImageSelect={(imageUrl) => {
            setStockImageUrl(imageUrl);
            setStockImageDialogOpen(false);
          }}
        />
      )}
    </>
  );
}
